import * as uuid from 'uuid';
import EventService from '../../helpers/classes/EventService';
import { ExceptionManager } from '../../helpers/classes/ExceptionManager';
import { HttpResponse } from '../../helpers/classes/HttpResponse';
import HttpService from '../../helpers/classes/HttpService';
import StorageService from '../../helpers/classes/StorageService';
import { STORAGE_KEYS } from '../configuration/StorageKeys';
import { CurrentResult } from '../enums/CurrentResult';
import { DocumentSource } from '../enums/DocumentSource';
import { FlowState } from '../enums/FlowState';
import { HighLevelResult } from '../enums/HighLevelResult';
import { JourneyEvent } from '../enums/JourneyEvent';
import { JourneyState } from '../enums/JourneyState';
import { TranslationKey } from '../enums/Translation';
import { ICropResponse } from '../interfaces/CropResponse';
import { IInputImage } from '../interfaces/InputImage';
import { IJourneyInformation } from '../interfaces/Journey';
import { UploadResponse } from '../interfaces/UploadResponse';
import { JourneyFlow } from './JourneyFlow';
import { IAdditionalData } from '../interfaces/AdditionalData';
import { osName, osVersion, browserName, fullBrowserVersion, mobileVendor, mobileModel, isMobile } from 'mobile-device-detect';
import { IMetaData } from '../interfaces/MetaData';
import { JourneyStep } from '../enums/JourneyStep';
import JourneyManager from './JourneyManager';
import { Mode } from '../enums/Mode';
import { IConfidoInfo } from '../interfaces/ConfidoInfo';
import DataService, { DataStoreType } from './DataService';
import { IIdScanData } from '../interfaces/DataFormats';

//global version string
declare var VERSION: string;

/**
 * @internal
 */
export class Journey {

	/**
	 * Generate a guid-like id and return it.
	 */
	protected static generateId = (): string => {
		return uuid.v4();
	}

	public get endDate(): Date {
		return this._endDate!;
	}

	public get id(): string {
		return this._id;
	}

	public get glareCheck(): string {
		return this._glareCheck!;
	}

	public get blurCheck(): string {
		return this._blurCheck!;
	}

	public get lowResolutionCheck(): string {
		return this._lowResolutionCheck!;
	}

	public get fullDocumentInViewCheck(): string {
		return this._fullDocumentInViewCheck!;
	}

	public get information(): IJourneyInformation {
		return this._information!;
	}

	public get isCancelled(): boolean {
		return this._isCancelled;
	}

	public get isFinished(): boolean {
		return this._isFinished;
	}

	public get additionalData(): IAdditionalData[] {
		return this._additionalData;
	}

	public get startDate(): Date {
		return this._startDate!;
	}

	public get reference(): string | undefined {
		return this._reference;
	}

	public get result(): HighLevelResult {
		return this._result;
	}

	public get status(): JourneyState {
		return this._status!;
	}

	public get totalRequests(): number {
		return this._totalRequests;
	}

	public get action(): FlowState {
		return this._flow.state;
	}

	public get actionAttempt(): number {
		return this._flow.attempt;
	}

	public get tripleScanReason(): string {
		return this._tripleScanReason!;
	}

	public get confidoInfo(): IConfidoInfo {
		return this._confidoInfo!;
	}

	private _endDate?: Date;
	private _id: string;
	private _information?: IJourneyInformation;
	private _inProgress: boolean = false;
	private _isCancelled: boolean = false;
	private _isFinished: boolean = false;
	private _additionalData: IAdditionalData[];
	private _metaData: IMetaData[] = [];
	private _reference: string | undefined;
	private _result: HighLevelResult = HighLevelResult.Undefined;
	private _startDate?: Date;
	private _status?: JourneyState;
	private _totalRequests: number = 0;
	private _flow: JourneyFlow = new JourneyFlow();
	private _glareCheck?: string;
	private _blurCheck?: string;
	private _lowResolutionCheck?: string;
	private _fullDocumentInViewCheck?: string;
	private _tripleScanReason?: string | undefined;
	private _confidoInfo?: IConfidoInfo;

	headerToken = StorageService.get(STORAGE_KEYS.journeyId);
	constructor(data?: IAdditionalData[], journeyId: string = Journey.generateId()) {
		if (!this.headerToken) { this._id = journeyId; }
		else { this._id = StorageService.get(STORAGE_KEYS.journeyId)!; }
		this._additionalData = data ? data : [];
	}

	/**
	 * Sets up the request object with the given parameters and sends it.
	 * Returns the promise of the request.
	 *
	 * @internal
	 */
	public continue = (image: {
		imgId?: string;
		imgList?: IInputImage[];
		coordinates?: number[][];
		excludeOutputImages?: boolean;
		isDocumentExtracted?: boolean;
		journeyDefinitionId: string;
	}, metaData?: IMetaData[]
	): Promise<UploadResponse> => {
		return this.assertIsNotInProgress()
			.then(this.assertUserAuthenticated)
			.then(this.assertJourneyNotEnded)
			.then(() => {
				if (image.imgId) {
					return this.assertImgIdExists(image.imgId)
						.then(() => this.assertCoordinatesExists(image.coordinates))
						.then(() => this.sendWithCoordinates(image.imgId!, image.coordinates!, image.excludeOutputImages, image.journeyDefinitionId, metaData));
				} else {
					return this.assertImgListExists(image.imgList)
						.then(() => this.send(image.imgList!, image.coordinates, image.isDocumentExtracted, image.excludeOutputImages, image.journeyDefinitionId, metaData));
				}
			});
	}

	/**
	* Sets up the request object with the given parameters and sends it.
	* Returns the promise of the request.
	*
	* @internal
	*/
	public continueCovid = (Base64Input: string,
		journeyDefinitionId?: string,
	): Promise<UploadResponse> => {
		return this.assertIsNotInProgress()
			.then(this.assertUserAuthenticated)
			.then(this.assertJourneyNotEnded)
			.then(() => {
				return this.sendCovid(Base64Input, journeyDefinitionId);
			});
	}

	/**
* Sets up the request object with the given parameters and sends it.
* Returns the promise of the request.
*
* @internal
*/
	public continueDataChecks = (ConfidoInfo: IConfidoInfo): Promise<IConfidoInfo> => {
		return this.assertIsNotInProgress()
			.then(this.assertUserAuthenticated)
			.then(this.assertJourneyNotEnded)
			.then(() => {
				return this.sendDataChecks(ConfidoInfo);
			});
	}

	/**
	 * Changes journey status to 'ENDED'.
	 * Cancelled journeys cannot send more requests.
	 */
	public cancel = (highLevelResult: HighLevelResult = HighLevelResult.Aborted): Promise<void> => {
		return this.assertUserAuthenticated()
			.then(() => {
				this._isCancelled = true;
				this._status = JourneyState.ENDED;
				this._result = highLevelResult;
				this._endDate = new Date();
			})
			.then(() => EventService.publish(JourneyEvent.JOURNEY_CANCEL, { id: this.id }));
	}

	/**
	 * Crop the image.
	 */

	public crop = (img: IInputImage): Promise<ICropResponse> => HttpService.post(HttpService.endpoints.crop, img, undefined, 'crop')
		.catch((e) => {
			if (e.response && e.response.status === 401) {
				throw ExceptionManager.process(new ExceptionManager.AuthorizationException(TranslationKey.EXCEPTION_AUTHORIZATION_ACCOUNT_NOT_AUTHORIZED, e.response));
			} else {
				throw e;
			}

		})

	/**
	 * A method for socket response to be integrated to a journey internally.
	 *
	 * @internal
	 */
	public bindUpdate = (res: any) => this.process(res);

	/**
	 * Check out the response from server and update the journey accordingly.
	 *
	 * @internal
	 */
	public update = (res: HttpResponse<UploadResponse>) => this.process(res instanceof HttpResponse ? res.data : res);

	/**
	 * Get the username
	 *
	 * @internal
	 */
	public getUsername = () => StorageService.get(STORAGE_KEYS.userName);

	/**
	 * Get the current state of the journey
	 *
	 * @internal
	 */
	public getState = () => {
		return {
			action: this.action,
			actionAttempt: this.actionAttempt
		};
	}

	/**
	 * Starts the journey
	 *
	 * @internal
	 */
	public start = (): Promise<this> => {
		this._startDate = new Date();
		this._status = JourneyState.STARTED;
		StorageService.set(STORAGE_KEYS.journeyId,this.id,24);
		return EventService.publish(JourneyEvent.JOURNEY_START, { id: this.id })
			.then(() => this);
	}

	/**
	 * @internal
	 */
	private send = (
		imgList: IInputImage[],
		coordinates?: number[][],
		isDocumentExtracted: boolean = typeof coordinates !== 'undefined',
		excludeOutputImages: boolean = true,
		journeyDefinitionId?: string,
		metaData?: IMetaData[]
	): Promise<any> => {
		metaData = metaData === undefined ? [] : metaData;
		this.setMetaData(metaData);
		return EventService.publish(JourneyEvent.JOURNEY_PROGRESS, { id: this.id })
			.then(() => {
				this._inProgress = true;
			})

			.then(() => HttpService.post(HttpService.endpoints.upload, {
				...(this.id ? { journeyId: this.id } : {}),
				PersonEntryId: this.id,
				additionalData: [{
					name: 'Platform',
					value: JourneyManager.mode === Mode.WEB ? 'WJCS' : Mode.CAPTURESTUDIO.toLowerCase()
				}, {
					name: 'UserName',
					value: this.getUsername()
				},
				...(journeyDefinitionId ? [{
					name: 'JourneyDefinitionId',
					value: journeyDefinitionId
				}] : []),
				...this._additionalData
				],
				metadata: this.getMetaData(),
				coordinates,
				excludeOutputImagesFromResponse: excludeOutputImages !== undefined ? excludeOutputImages : true,
				inputImages: imgList,
				isDocumentExtracted,
				source: this.inferSource(),
			}, {}, 'upload'))
			.then((res) => {
				this._inProgress = false;
				this._metaData.splice(0, this._metaData.length);
				return this.update(res);
			})
			.catch((e) => {
				this._inProgress = false;
				return Promise.reject(e);
			});
	}

	/**
	 * @internal
	 */
	private sendCovid = (
		Base64Input: string,
		journeyDefinitionId?: string,

	): Promise<any> => {

		return EventService.publish(JourneyEvent.JOURNEY_PROGRESS, { id: this.id })
			.then(() => {
				this._inProgress = true;
			})

			.then(() => HttpService.post(HttpService.endpoints.covidpass,
				JSON.stringify({
					Base64Input: Base64Input,
					JourneyId: journeyDefinitionId,
				}),
				undefined,
				'covid'))
			.then((res) => {
				this._inProgress = false;
				return this.update(res);
			})
			.catch((e) => {
				this._inProgress = false;
				return Promise.reject(e);
			});
	}

	/**
* @internal
*/
	private sendDataChecks = (
		ConfidoInfo: IConfidoInfo
	): Promise<any> => {

		return EventService.publish(JourneyEvent.JOURNEY_PROGRESS, { id: this.id })
			.then(() => {
				this._inProgress = true;
			})
			.then(() => HttpService.post(HttpService.endpoints.dataChecks,
				JSON.stringify({
					journeyId: ConfidoInfo.journeyId,
					houseName: ConfidoInfo.houseName,
					streetName: ConfidoInfo.streetName,
					town: ConfidoInfo.town,
					postCode: ConfidoInfo.postCode
				}),
				undefined,
				'dataChecks'
			))
			.then((res) => {
				this._inProgress = false;
				this._confidoInfo = ConfidoInfo;
				return this.update(res);
			})
			.catch((e) => {
				this._inProgress = false;
				return Promise.reject(e);
			});
	}

	/**
	 * @internal
	 */
	private sendWithCoordinates = (
		imgId: string,
		coordinates: number[][],
		excludeOutputImages?: boolean,
		journeyDefinitionId?: string,
		metaData?: IMetaData[]
	): Promise<any> => EventService.publish(JourneyEvent.JOURNEY_PROGRESS, { id: this.id })
		.then(() => {
			this._inProgress = true;
			metaData = metaData === undefined ? [] : metaData;
			this.setMetaData(metaData);
		})
		.then(() => HttpService.post(HttpService.endpoints.upload, {
			...(this.id ? { journeyId: this.id } : {}),
			additionalData: [{
				name: 'Platform',
				value: JourneyManager.mode === Mode.WEB ? "WJCS" : Mode.CAPTURESTUDIO.toLowerCase()
			}, {
				name: 'UserName',
				value: this.getUsername()
			},
			...(journeyDefinitionId ? [{
				name: 'JourneyDefinitionId',
				value: journeyDefinitionId
			}] : []),
			...this._additionalData
			],
			metadata: this.getMetaData(),
			coordinates,
			excludeOutputImagesFromResponse: excludeOutputImages !== undefined ? excludeOutputImages : true,
			inputImageId: imgId,
			isDocumentExtracted: true,
			source: this.inferSource()
		}, {}, 'send'))
		.then((res) => {
			this._inProgress = false;
			return this.update(res);
		})
		.catch((e) => {
			this._inProgress = false;
			throw e;
		})

	/**
	 * Checking information object for properties, and merging it with the local information object.
	 */
	private checkInformation = (info: IJourneyInformation): Promise<void> => new Promise((resolve) => {
		this._information = this._information ? this._information : {
			blurCheck: undefined,
			documents: [],
			glareCheck: undefined,
			holder: {
				address: {
					city: undefined,
					full: undefined,
					postCode: undefined
				},
				birthDate: undefined,
				birthPlace: undefined,
				firstName: undefined,
				fullName: undefined,
				gender: undefined,
				lastName: undefined,
				middleName: undefined
			},
			lowResolutionCheck: undefined,
			fullDocumentInViewCheck: undefined,
			scanReference: undefined,
			scannerIp: undefined,
			tripleScanReason: undefined
		};

		if (info.documents) {
			this._information.documents.push(...info.documents);
		}

		if (info.holder) {
			if (info.holder.address) {
				this._information.holder.address.city = info.holder.address.city || this._information.holder.address.city;
				this._information.holder.address.full = info.holder.address.full || this._information.holder.address.full;
				this._information.holder.address.postCode = info.holder.address.postCode || this._information.holder.address.postCode;
			}

			if (info.holder.birthDate) {
				this._information.holder.birthDate = info.holder.birthDate || this._information.holder.birthDate;
			}

			if (info.holder.birthPlace) {
				this._information.holder.birthPlace = info.holder.birthPlace || this._information.holder.birthPlace;
			}

			if (info.holder.firstName) {
				this._information.holder.firstName = info.holder.firstName || this._information.holder.firstName;
			}

			if (info.holder.fullName) {
				this._information.holder.fullName = info.holder.fullName || this._information.holder.fullName;
			}

			if (info.holder.gender) {
				this._information.holder.gender = info.holder.gender || this._information.holder.gender;
			}

			if (info.holder.lastName) {
				this._information.holder.lastName = info.holder.lastName || this._information.holder.lastName;
			}

			if (info.holder.middleName) {
				this._information.holder.middleName = info.holder.middleName || this._information.holder.middleName;
			}
		}

		if (info.scannerIp) {
			this._information.scannerIp = info.scannerIp || this._information.scannerIp;
		}

		if (info.glareCheck) {
			this._information.glareCheck = info.glareCheck || this._information.glareCheck;
		}

		if (info.blurCheck) {
			this._information.blurCheck = info.blurCheck || this._information.blurCheck;
		}

		if (info.lowResolutionCheck) {
			this._information.lowResolutionCheck = info.lowResolutionCheck || this._information.lowResolutionCheck;
		}

		if (info.fullDocumentInViewCheck) {
			this._information.fullDocumentInViewCheck = info.fullDocumentInViewCheck || this._information.fullDocumentInViewCheck;
		}

		if (info.scanReference) {
			this._information.scanReference = info.scanReference || this._information.scanReference;
		}

		if (info.tripleScanReason) {
			this._information.tripleScanReason = info.tripleScanReason || this._information.tripleScanReason;
		}
		return resolve();
	})

	/**
	 * The main method that inspects the response and updates the related fields
	 *
	 * @internal
	 */
	private process = (data: UploadResponse): Promise<UploadResponse> => {
		this._totalRequests++;

		if (data) {
			return this.assertNoErrors(data.hasError)
				.then(() => this._flow.proceed(data.requiredAction))
				.then(() => this.checkInformation(data.entryData))
				.then(() => {
					const { entryData: { scanReference, glareCheck, blurCheck, lowResolutionCheck, fullDocumentInViewCheck, tripleScanReason } = {} as any } = data;

					this._reference = scanReference;
					this._glareCheck = glareCheck;
					this._blurCheck = blurCheck;
					this._lowResolutionCheck = lowResolutionCheck;
					this._fullDocumentInViewCheck = fullDocumentInViewCheck;
					this._tripleScanReason = tripleScanReason;
					if (data.personEntryId !== undefined && !data.journeyId) {
						this._id = data.personEntryId;
					}
					if (data.isFinished === true) {
						this._status = JourneyState.ENDED;
						this._isFinished = true;
						this._endDate = new Date();
						this._result = this.parseResult(data);

						new Promise(() => {
							setTimeout(() => {
								return EventService.publish(JourneyEvent.JOURNEY_END, { id: this.id })
							}, 200)
						})

					} else if (this.totalRequests > 0) {
						this._status = JourneyState.INPROGRESS;
						EventService.publish(JourneyEvent.JOURNEY_PROGRESS, { id: this.id });
						return;
					}
				})
				.then(() => {
					if (data.personEntryId !== undefined && !data.journeyId) {
						this._id = data.personEntryId;
					}
				})
				.then(() => data);
		} else {
			return Promise.resolve(data);
		}
	}

	private parseResult = ({ highLevelResult }: any) => {
		switch (highLevelResult) {
			case 'Passed':
				return HighLevelResult.Passed;
			case 'Refer':
				return HighLevelResult.Refer;
			case 'Expired':
				return HighLevelResult.Expired;
			case 'NotAccepted':
				return HighLevelResult.NotAccepted;
			case 'Notsupported':
				return HighLevelResult.NotSupported;
			default:
				return HighLevelResult.Undefined;
		}
	}

	/**
	 * Infer the source of the request
	 */
	private inferSource = (): DocumentSource => this._flow.state === FlowState.ADDRESS ? DocumentSource.ADDRESS : DocumentSource.IDENTITY;

	/**
	 * Check if the user is authorized for this process, if not throw an exception .
	 */
	private assertUserAuthenticated = (): Promise<void> => new Promise((resolve, reject) => {
		if (HttpService.auth !== true && HttpService.authSettings !== false) {
			return reject(ExceptionManager.process(new ExceptionManager.AuthorizationException(TranslationKey.EXCEPTION_AUTHORIZATION_PROCESS_NOT_AUTHORIZED)));
		} else {
			return resolve();
		}
	})

	/**
	 * Check if the journey has ended to send requests, if not throw an exception .
	 */
	private assertJourneyNotEnded = (): Promise<void> => new Promise((resolve, reject) => {
		if (this._status === JourneyState.ENDED) {
			return reject(ExceptionManager.process(new ExceptionManager.FlowStateException(TranslationKey.EXCEPTION_FLOW_STATE_JOURNEY_ENDED)));
		} else {
			return resolve();
		}
	})

	/**
	 * Supplied parameters must contain coordinates.
	 *
	 * @internal
	 */
	private assertCoordinatesExists = (coordinates?: number[][]): Promise<void> => new Promise((resolve, reject) => {
		if (coordinates === undefined) {
			return reject(ExceptionManager.process(new ExceptionManager.MissingParameterException(TranslationKey.EXCEPTION_MISSING_PARAMETER_COORDINATES_MISSING)));
		} else {
			return resolve();
		}
	})

	/**
	 * Supplied parameters must contain image.
	 *
	 * @internal
	 */
	private assertImgIdExists = (imgId?: string): Promise<void> => new Promise((resolve, reject) => {
		if (imgId === undefined || imgId === '') {
			return reject(ExceptionManager.process(new ExceptionManager.MissingParameterException(TranslationKey.EXCEPTION_MISSING_PARAMETER_IMAGES_MISSING)));
		} else {
			return resolve();
		}
	})

	/**
	 * Supplied parameters must contain input images.
	 *
	 * @internal
	 */
	private assertImgListExists = (imgList: IInputImage[] = []): Promise<void> => new Promise((resolve, reject) => {
		if (!imgList.length) {
			return reject(ExceptionManager.process(new ExceptionManager.MissingParameterException(TranslationKey.EXCEPTION_MISSING_PARAMETER_IMAGES_MISSING)));
		} else {
			return resolve();
		}
	})

	/**
	 * Assert if hasError is true to throw an exeption
	 * Returns the promise of the request.
	 *
	 * @internal
	 */
	private assertNoErrors = (hasError: boolean): Promise<void> => new Promise((resolve, reject) => {
		if (hasError === true) {
			return reject(ExceptionManager.process(new ExceptionManager.ServerException(TranslationKey.EXCEPTION_SERVER_DATA_NOT_PERSISTED)));
		} else {
			return resolve();
		}
	})

	/**
	 * Assert if current result is 'Pass' or undefined
	 *
	 * @internal
	 */
	private assertCurrentResult = (cr: string = CurrentResult.Pass): Promise<void> => new Promise((resolve, reject) => {
		if (cr !== CurrentResult.Pass) {
			return reject(ExceptionManager.process(new ExceptionManager.ServerException(TranslationKey.EXCEPTION_SERVER_RESULT_NOT_PASSING)));
		} else {
			return resolve();
		}
	})

	/**
	 * Assert if the journey is waiting for a response
	 */
	private assertIsNotInProgress = (): Promise<void> => new Promise((resolve, reject) => {
		if (this._inProgress) {
			return reject(ExceptionManager.process(new ExceptionManager.BadRequestException(TranslationKey.EXCEPTION_BAD_REQUEST_WAITING_FOR_RESPONSE)));
		} else {
			return resolve();
		}
	})

	private setMetaData(metaData: IMetaData[]): void {
		let step: string = this._flow.state;

		switch (step) {
			case FlowState.IDENTITY_FRONT:
				step = JourneyStep.FRONTSIDE;
				break;
			case FlowState.IDENTITY_BACK:
				step = JourneyStep.BACKSIDE;
				break;
			case FlowState.ADDRESS:
				step = JourneyStep.ADDRESSDOCUMENT;
				break;
			case FlowState.COVIDPASS:
				step = JourneyStep.COVIDPASS;
				break;
			case FlowState.DATACHECKS:
				step = JourneyStep.DATACHECKS;
				break;
			default:
				break;
		}

		metaData.forEach((element) => {
			if (element.step === '') {
				element.step = step;
			}
		});
		this._metaData.push(...metaData);
	}
	private getMetaData(): IMetaData[] {

		if (this._flow.state === FlowState.IDENTITY_FRONT) {
			// will send MetaData for first request(Identity front)

			const generalMetaData = [{
				name: 'Channel', // Mobile, Web, or Capture Studio
				value: 'Web',
				step: 'GENERAL',
				type: 'Simple'
			}, {
				name: 'Web Sdk Version', // Version Number of WEB SDK
				value: VERSION,
				step: 'GENERAL',
				type: 'Simple'
			}, {
				name: 'Platform', // Windows, MacOS, Android, iOS, Linux, etc
				value: osName,
				step: 'GENERAL',
				type: 'Simple'
			}, {
				name: 'Platform Version', // The version number of the above-mentioned platform
				value: osVersion,
				step: 'GENERAL',
				type: 'Simple'
			}, {
				name: 'Browser', // Chrome, Safari, etc
				value: browserName,
				step: 'GENERAL',
				type: 'Simple'
			}, {
				name: 'Browser Version', // Browser version
				value: fullBrowserVersion,
				step: 'GENERAL',
				type: 'Simple'
			}];

			this._metaData.push(...generalMetaData);
		}

		if (JourneyManager.mode === Mode.CAPTURESTUDIO) {

			const captureType = this._metaData.filter((ct) => ct.name === 'Capture Type');

			this._metaData.splice(0, this._metaData.length);
			this._metaData.push(...captureType);

			const captureStudioMetaData = [{
				name: 'Platform', // Windows, MacOS, Android, iOS, Linux, etc
				value: osName,
				step: 'GENERAL',
				type: 'Simple'
			}, {
				name: 'Platform Version', // The version number of the above-mentioned platform
				value: osVersion,
				step: 'GENERAL',
				type: 'Simple'
			}, {
				name: 'Browser', // Chrome, Safari, etc
				value: browserName,
				step: 'GENERAL',
				type: 'Simple'
			}, {
				name: 'Browser Version', // Browser version
				value: fullBrowserVersion,
				step: 'GENERAL',
				type: 'Simple'
			}];

			this._metaData.push(...captureStudioMetaData);
		}

		return this._metaData;
	}
}
